import { useEffect, useState } from 'react';

import { Download, ExternalLink, FileText } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { useParams } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { publicDocumentApi, type PublicDocumentMeta } from '@/lib/api/public-document.api';
import { useBrandingStore } from '@/stores/branding.store';

function formatBytes(bytes: number | null): string {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function isMobile(): boolean {
  return /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
}

const PdfCard = ({
  name,
  downloadUrl,
  contentUrl,
}: {
  name: string;
  downloadUrl: string;
  contentUrl: string;
}) => (
  <div className="flex flex-col items-center gap-6 rounded-lg bg-white p-8 shadow-sm dark:bg-gray-900">
    <FileText className="h-16 w-16 text-gray-400" />
    <h2 className="text-center text-lg font-medium text-gray-900 dark:text-gray-100">{name}</h2>
    <div className="flex gap-3">
      <a href={contentUrl} target="_blank" rel="noopener noreferrer">
        <Button className="gap-1.5">
          <ExternalLink className="h-4 w-4" />
          Open PDF
        </Button>
      </a>
      <a href={downloadUrl} download>
        <Button variant="outline" className="gap-1.5">
          <Download className="h-4 w-4" />
          Download
        </Button>
      </a>
    </div>
  </div>
);

const PublicDocumentPage = () => {
  const { token } = useParams<{ token: string }>();
  const [meta, setMeta] = useState<PublicDocumentMeta | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [textContent, setTextContent] = useState<string | null>(null);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const { companyName, logoUrl, logoUrlDark } = useBrandingStore();
  const isDark =
    typeof window !== 'undefined' && document.documentElement.classList.contains('dark');
  const brandLogo = isDark && logoUrlDark ? logoUrlDark : logoUrl;
  const mobile = isMobile();

  useEffect(() => {
    if (!token) return;

    let cancelled = false;

    const load = async () => {
      try {
        const data = await publicDocumentApi.getMeta(token);
        if (cancelled) return;
        setMeta(data);

        if (data.kind === 'text' || data.kind === 'markdown') {
          try {
            const r = await fetch(publicDocumentApi.getContentUrl(token));
            const text = await r.text();
            if (!cancelled) setTextContent(text);
          } catch {
            if (!cancelled) setTextContent('Failed to load content');
          }
        }

        if (!mobile && (data.kind === 'pdf' || data.kind === 'image')) {
          try {
            const r = await fetch(publicDocumentApi.getContentUrl(token));
            const blob = await r.blob();
            if (!cancelled) setBlobUrl(URL.createObjectURL(blob));
          } catch {
            // blob fetch failed — download button still works
          }
        }
      } catch (err: unknown) {
        if (cancelled) return;
        const status = (err as { response?: { status?: number } })?.response?.status;
        if (status === 404) {
          setError('This document is no longer available or the link has expired.');
        } else {
          setError('Something went wrong. Please try again later.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 dark:bg-gray-950">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-gray-600" />
      </div>
    );
  }

  if (error || !meta || !token) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-gray-50 px-4 dark:bg-gray-950">
        <FileText className="h-12 w-12 text-gray-400" />
        <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
          Document not found
        </h1>
        <p className="max-w-md text-center text-gray-500">
          {error || 'The link you followed is invalid or has expired.'}
        </p>
      </div>
    );
  }

  const contentUrl = publicDocumentApi.getContentUrl(token);
  const downloadUrl = publicDocumentApi.getContentUrl(token, true);

  return (
    <div className="flex min-h-screen flex-col bg-gray-50 dark:bg-gray-950">
      {/* Header */}
      <header className="sticky top-0 z-10 border-b bg-white px-4 py-3 shadow-sm dark:border-gray-800 dark:bg-gray-900">
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-3">
            {brandLogo ? (
              <img src={brandLogo} alt={companyName} className="h-7 shrink-0" />
            ) : companyName ? (
              <span className="shrink-0 text-sm font-bold text-gray-900 dark:text-gray-100">
                {companyName}
              </span>
            ) : (
              <FileText className="h-5 w-5 shrink-0 text-gray-500" />
            )}
            <div className="bg-border hidden h-5 w-px sm:block" />
            <div className="min-w-0">
              <h1 className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">
                {meta.displayName}
              </h1>
              {meta.sizeBytes && (
                <p className="text-xs text-gray-500">{formatBytes(meta.sizeBytes)}</p>
              )}
            </div>
          </div>
          <a href={downloadUrl} download>
            <Button variant="outline" size="sm" className="gap-1.5">
              <Download className="h-4 w-4" />
              Download
            </Button>
          </a>
        </div>
      </header>

      {/* Content */}
      <main className="mx-auto w-full max-w-5xl flex-1 p-4">
        {meta.kind === 'pdf' &&
          (mobile ? (
            <PdfCard name={meta.displayName} downloadUrl={downloadUrl} contentUrl={contentUrl} />
          ) : blobUrl ? (
            <iframe src={blobUrl} className="h-[calc(100vh-80px)] w-full rounded border" />
          ) : (
            <div className="flex flex-col items-center gap-4 rounded-lg bg-white p-8 shadow-sm dark:bg-gray-900">
              <FileText className="h-16 w-16 text-gray-400" />
              <p className="text-sm text-gray-500">Loading PDF…</p>
            </div>
          ))}

        {meta.kind === 'image' &&
          (mobile ? (
            <div className="flex justify-center">
              <img
                src={contentUrl}
                alt={meta.displayName}
                className="max-h-[80vh] rounded object-contain"
              />
            </div>
          ) : blobUrl ? (
            <div className="flex justify-center">
              <img
                src={blobUrl}
                alt={meta.displayName}
                className="max-h-[80vh] rounded object-contain"
              />
            </div>
          ) : (
            <div className="flex flex-col items-center gap-4 rounded-lg bg-white p-8 shadow-sm dark:bg-gray-900">
              <FileText className="h-16 w-16 text-gray-400" />
              <p className="text-sm text-gray-500">Loading image…</p>
            </div>
          ))}

        {meta.kind === 'markdown' && textContent !== null && (
          <div className="prose prose-sm dark:prose-invert mx-auto max-w-3xl rounded-lg bg-white p-6 shadow-sm dark:bg-gray-900">
            <ReactMarkdown>{textContent}</ReactMarkdown>
          </div>
        )}

        {meta.kind === 'text' && textContent !== null && (
          <pre className="max-h-[80vh] overflow-auto rounded-lg bg-white p-6 font-mono text-sm shadow-sm dark:bg-gray-900 dark:text-gray-200">
            {textContent}
          </pre>
        )}

        {meta.kind === 'office' && (
          <div className="flex flex-col items-center gap-4 rounded-lg bg-white p-8 shadow-sm dark:bg-gray-900">
            <FileText className="h-16 w-16 text-gray-400" />
            <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">
              {meta.displayName}
            </h2>
            <p className="text-sm text-gray-500">
              This file type cannot be previewed in the browser.
            </p>
            <a href={downloadUrl} download>
              <Button className="gap-1.5">
                <Download className="h-4 w-4" />
                Download file
              </Button>
            </a>
          </div>
        )}

        {meta.kind === 'unsupported' && (
          <div className="flex flex-col items-center gap-4 rounded-lg bg-white p-8 shadow-sm dark:bg-gray-900">
            <FileText className="h-16 w-16 text-gray-400" />
            <p className="text-sm text-gray-500">This file type is not supported for preview.</p>
            <a href={downloadUrl} download>
              <Button variant="outline" className="gap-1.5">
                <Download className="h-4 w-4" />
                Download
              </Button>
            </a>
          </div>
        )}
      </main>
    </div>
  );
};

export default PublicDocumentPage;
